import React, { useState, useEffect, useRef, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Field } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { BaseField } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { FieldMessage } from '@atlassian/jira-issue-create-commons/src/common/ui/field-message/index.tsx';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
	fireUIAnalytics,
	ContextualAnalyticsData,
	INLINE_DIALOG,
} from '@atlassian/jira-product-analytics-bridge';
import {
	UNSUPPORTED_FIELD_TYPES,
	ERROR_ICON_TEST_ID,
	MESSAGE_CONTAINER_TEST_ID,
} from './constants.tsx';
import messages from './messages.tsx';

const IncompatibleField = (
	props: BaseField & {
		projectType?: ProjectType;
		gqlType: string;
		fieldType: string;
		onFieldMount: (fieldId: string) => void;
		isMini?: boolean;
	},
) => {
	const isMountEventFired = useRef(false);
	const [isInfoMessageOpen, setInfoMessageOpen] = useState<boolean>(false);
	const {
		fieldId,
		fieldName,
		isRequired = false,
		description,
		projectType,
		gqlType,
		fieldType,
		onFieldMount,
		isMini,
	} = props;

	useEffect(() => {
		if (isMountEventFired.current === false) {
			isMountEventFired.current = true;
			onFieldMount(fieldId);
		}
	}, [onFieldMount, fieldId]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const closeInfoMessage = () => {
		setInfoMessageOpen(false);
	};

	const toggleInfoMessageVisibility = () => {
		if (isInfoMessageOpen === true) {
			closeInfoMessage();
			return;
		}
		setInfoMessageOpen(true);
		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'inlineDialog',
			action: 'viewed',
		});
		fireUIAnalytics(analyticsEvent, 'globalIssueCreateFieldUnsupported');
	};

	useEffect(() => {
		const analyticsEvent = createAnalyticsEvent({});
		fireTrackAnalyticsDeferred(analyticsEvent, 'unsupported newGicField', {
			gqlType,
			projectType,
			fieldType,
		});
	}, [createAnalyticsEvent, projectType, gqlType, fieldType]);

	return (
		<Field id={fieldId} label={fieldName} isRequired={isRequired} name={fieldId}>
			{({ fieldProps }) => (
				<>
					<InlineMessageContainer>
						<FieldContainer>
							<Textfield isDisabled aria-labelledby={fieldProps['aria-labelledby']} />
						</FieldContainer>

						{!UNSUPPORTED_FIELD_TYPES.includes(fieldType) && (
							<ContextualAnalyticsData
								sourceType={INLINE_DIALOG}
								sourceName="globalIssueCreateFieldUnsupported"
							>
								<InlineDialog
									content={
										<MessageContainer data-testid={MESSAGE_CONTAINER_TEST_ID}>
											<FormattedMessage
												{...messages.incompatibleFieldMessageNoOptionToSwitchToOldModal}
												values={{
													fieldName,
													b: (chunks) => <b>{chunks}</b>,
												}}
											/>
										</MessageContainer>
									}
									isOpen={isInfoMessageOpen}
									onClose={closeInfoMessage}
									messageId="issue-create.common.ui.fields.incompatible-field.inline-dialog"
									messageType="transactional"
								>
									<Button
										spacing="none"
										appearance="subtle"
										onClick={toggleInfoMessageVisibility}
										iconBefore={
											<ErrorIcon
												label="incompatible-field-info"
												LEGACY_size="medium"
												spacing="spacious"
												color={token('color.icon.subtle')}
											/>
										}
										testId={ERROR_ICON_TEST_ID}
									/>
								</InlineDialog>
							</ContextualAnalyticsData>
						)}
					</InlineMessageContainer>
					<FieldMessage description={description} fieldName={fieldName} isMini={isMini} />
				</>
			)}
		</Field>
	);
};

export default IncompatibleField;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineMessageContainer = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldContainer = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 43.75}px`,
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	width: '345px',
});
